import * as React from 'react';
import { createContext, useContext, useMemo } from 'react';

import { branch, compose } from 'recompose';

import { ProductVariantNames } from 'bundles/browse-common/constants';
import withPremiumProductCollections from 'bundles/browse-common/legacy/components/hoc/withPremiumProductCollections';
import type { DegreeProductMember } from 'bundles/browse-common/legacy/types/degree';
import type { ProfessionalCertificateProps } from 'bundles/browse/components/types/ProfessionalCertificate';
import withProfessionalCertificates from 'bundles/browse/components/withProfessionalCertificates';
import withMegaMenuData from 'bundles/megamenu/components/hoc/withMegaMenuData';
import withMegaMenuSkillData from 'bundles/megamenu/components/hoc/withMegaMenuSkillData';
import type { MegaMenuData, mappedMegaMenuAPIData } from 'bundles/megamenu/types/MenuData';
import { getMegaMenuSectionData } from 'bundles/megamenu/utils/MegaMenuUtils';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import type DomainsV1Type from 'bundles/naptimejs/resources/domains.v1';
import { withDegreesAndPrograms, withDomains } from 'bundles/page-header/utils/pageHeaderNavUtils';

// Context to provide MegaMenuData
const MegaMenuDataContext = createContext<MegaMenuData[] | undefined>(undefined);
const { BachelorsDegree, MastersDegree, Mastertrack } = ProductVariantNames;

type Props = {
  domains?: Array<DomainsV1Type>;
  degreeListFromPremiumProductsCollection?: DegreeProductMember[];
  mastertrackListFromPremiumProductsCollection?: DegreeProductMember[];
  professionalCertificates: ProfessionalCertificateProps[];
  megaMenuAPIData?: mappedMegaMenuAPIData;
} & { children: React.ReactNode };

export const MegaMenuDataProvider = ({
  domains,
  degreeListFromPremiumProductsCollection: degreeList = [],
  mastertrackListFromPremiumProductsCollection: mastertrackList = [],
  professionalCertificates,
  megaMenuAPIData,
  children,
}: Props) => {
  const megamenuData = useMemo(() => {
    if (domains?.length && megaMenuAPIData) {
      return getMegaMenuSectionData({
        domains,
        degreeList,
        mastertrackList,
        professionalCertificates,
        megaMenuAPIData,
      });
    }
    return undefined;
  }, [domains, degreeList, mastertrackList, professionalCertificates, megaMenuAPIData]);
  return <MegaMenuDataContext.Provider value={megamenuData}>{children}</MegaMenuDataContext.Provider>;
};

export const useMegaMenuData = () => useContext(MegaMenuDataContext);

type PropsToComponent = Props;
type PropsFromCaller = { isEnterprise: boolean; userId?: number };

export const MegaMenuDataProviderWithHOC = compose<PropsToComponent, React.PropsWithChildren<PropsFromCaller>>(
  branch<PropsFromCaller>(
    ({ isEnterprise }) => !isEnterprise,
    compose(
      withPremiumProductCollections(() => ({
        productVariants: [BachelorsDegree, MastersDegree, Mastertrack],
        ssr: false,
      })),
      withMegaMenuData(),
      withMegaMenuSkillData(),
      withProfessionalCertificates()
    )
  ),
  withDomains,
  // Should only make this call when the learner is signed in
  // For some reason, this only works when checking the user ID and not just with `isAuthenticatedUser()`
  branch<PropsFromCaller>(({ userId }) => userId !== undefined, withDegreesAndPrograms)
)(MegaMenuDataProvider);
